import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export default function Verify2FAPage({ verificationId, email, onBack, onSuccess }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pasted.length; i++) {
      newCode[i] = pasted[i];
    }
    setCode(newCode);
    const nextEmpty = pasted.length < 6 ? pasted.length : 5;
    inputRefs.current[nextEmpty]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullCode = code.join("");
    if (fullCode.length !== 6) {
      toast.error("Palun sisestage 6-kohaline kood");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/verify-2fa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: fullCode }),
      });

      const data = await response.json();
      
      if (response.ok && data.user) {
        if (data.session_token) {
          localStorage.setItem("session_token", data.session_token);
        }
        setUser(data.user);
        toast.success("Sisselogimine õnnestus!");
        if (onSuccess) onSuccess();
        navigate("/", { replace: true });
      } else {
        toast.error(data.detail || "Kinnitamine ebaõnnestus");
        setCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error("2FA verify error:", error);
      toast.error("Võrguühenduse viga. Proovige uuesti.");
      // Don't clear code on network error - let user retry
    } finally {
      setLoading(false);
    }
  };

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md border-slate-200">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Kinnituskood</CardTitle>
          <p className="text-sm text-slate-500 mt-2">
            Saatsime 6-kohalise koodi aadressile
          </p>
          <p className="text-sm font-medium text-slate-700">{email}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-center gap-2" onPaste={handlePaste}>
              {code.map((digit, i) => (
                <Input
                  key={`digit-${i}`}
                  ref={el => inputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  className="w-12 h-14 text-center text-2xl font-bold"
                  data-testid={`2fa-code-input-${i}`}
                />
              ))}
            </div>
            
            <div className="text-center">
              {timeLeft > 0 ? (
                <p className="text-sm text-slate-500">
                  Kood kehtib: {minutes}:{seconds.toString().padStart(2, "0")}
                </p>
              ) : (
                <p className="text-sm text-red-500">Kood on aegunud</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={loading || timeLeft === 0}
              data-testid="verify-2fa-submit"
            >
              {loading ? "Kontrollin..." : "Kinnita"}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={onBack}
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tagasi sisselogimisele
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
